<template>
  <div class="iDalgo-Section">
    <div class="iDalgo-Section__Header">
      <div>
        <h2>
          <el-input v-model="section.name"></el-input>
        </h2>
        <p v-html="sectionCounterHtml"></p>
      </div>
      <div class="iDalgo-Section__Header__Quantity">
        <el-input-number v-model="section.size" size="small" :min="1" :max="section.size + available"></el-input-number>
      </div>
      <span v-if="deletable" class="iDalgo-Section__Header__Delete" @click="handleDelete">
        <svgicon name="cross" :original="true"></svgicon>
      </span>
    </div>
    <draggable
      class="iDalgo-Section__Content"
      :class="{ 'empty': isEmpty }"
      v-model="section._players"
      :options="{group:'players', forceFallback: true}"
      @start="handleDraggableStart"
      @end="handleDraggableEnd"
      :move="handleDraggableMove">
      <transition-group name="fade">
        <i-dalgo-card
          v-for="player in section._players"
          :key="player.id"
          :picture="player.tUrlPicture"
          :title="player.tNickName"
          @click="handleDeletePlayer(player.id)"
          class="iDalgo-Section__Content__Player" />
      </transition-group>
    </draggable>
  </div>
</template>

<script>
import draggable from 'vuedraggable'
import iDalgoCard from '@/components/iDalgo-Card'
import '@/components/icons'

export default {
  name: 'iDalgo-Section',
  components: {
    draggable,
    iDalgoCard
  },
  props: {
    section: {
      type: Object,
      required: true
    },
    available: {
      type: [Number, Boolean]
    },
    deletable: {
      type: Boolean,
      required: true
    }
  },
  computed: {
    isEmpty () {
      if (this.section._players.length > 0) {
        return false
      }
      return true
    },
    title () {
      return `${this.$t('iDalgoSection.title')} ${this.section.size}`
    },
    counter () {
      return `<b>${this.available}</b> ${this.$t('iDalgoSection.possibilities')}`
    },
    sectionSize () {
      return this.section.size
    },
    sectionCounterHtml () {
      return this.$t('iDalgoListEdit.title.description', {
        listSize: `<span class="red"> ${this.section.size} </span>`,
        totalPlayers: `<span class="red"> ${this.playersNumber} </span>`
      })
    },
    playersNumber () {
      return this.section._players.length
    },
    isAvailable () {
      return (this.size > 0)
    }
  },
  data () {
    return {
      quantity: 3
    }
  },
  methods: {
    handleDraggableMove (evt) {
      // prevent order in selection list
      document.querySelectorAll('.iDalgo-Section__Content').forEach((el) => {
        el.classList.remove('dragzone')
      })
      evt.to.offsetParent.classList.add('dragzone')
      return (evt.to.offsetParent.classList[0] === 'iDalgo-Section__Content')
    },
    handleDraggableStart () {
      this.$store.commit('players/DRAGGING_UPDATED', true)
    },
    handleDraggableEnd () {
      document.querySelectorAll('.dragzone').forEach((el) => {
        el.classList.remove('dragzone')
      })
      this.$store.commit('players/DRAGGING_UPDATED', false)
    },
    handleDeletePlayer (id) {
      this.section._players = this.section._players.filter(p => p.id !== id)
    },
    handleDelete () {
      this.$confirm(this.$t('iDalgoDialogs.deleteSection.message'), this.$t('iDalgoDialogs.deleteSection.title'), {
        confirmButtonText: this.$t('actions.delete'),
        cancelButtonText: this.$t('actions.cancelButton'),
        type: 'warning'
      }).then(() => {
        this.$emit('delete', this.section)
      }).catch(() => {})
    }
  },
  mounted () {
    this.$el.querySelector('input').focus()
    document.querySelector('.el-icon-plus').className = 'icon el-icon-caret-top'
    document.querySelector('.el-icon-minus').className = 'icon el-icon-caret-bottom'
  }
}
</script>

<style lang="scss">
.iDalgo-Section {
  width: 100%;
  min-height: 287px;
  border-radius: 3px;
  background-color: #FFFFFF;
  padding: 30px ($default_padding * 2) ($default_padding * 2) ($default_padding * 2);
  margin-bottom: $default_padding * 2;
  box-sizing: border-box;
  user-select: none;

  &__Header {
    display: flex;
    justify-content: space-between;
    margin-bottom: $default_padding * 3;
    position: relative;

    div {
      p {
        color: #B2B2B2;
        font-size: 16px;
        font-family: 'robotoregular';
        font-weight: normal;
        .red {
          color: $color2;
        }
      }

      h2 {
        width: 80%;
        margin: 0;
        padding: 0;

        input {
          border: none;
          padding: 0;
          color: $color1;
          width: 200px;
          font-size: 28px;
          transition: all 0.2s;
          border-radius: 0;
          transform: translateX(0);
          font-size: 32px;
          font-family: 'robotobold';
          color: $color1;

          &:focus {
            box-shadow: 0 2px 0 $color1;
            transform: translateX(32px);
          }
        }

        &:hover {
          input {
            left: 0;
            box-shadow: 0 2px 0 $color1;
            transform: translateX(32px);
          }
        }
      }
    }

    &__Quantity {
      width: 30%;
      flex-flow: column;
      display: flex;
      align-items: flex-end;

      .el-input-number {
        &__decrease, &__increase {
          height: 30px;
          width: 30px;
          border: 1px solid #2C2E54;
          border-radius: 4px;
          background-color: #F5F5F7;
        }
        .icon {
          font-size: 16px;
          color: $color1
        }
        .el-input, .el-input__inner {
          border: none;
          color: #90A4AE;
          font-family: "robotobold";
          font-size: 32px;
          text-align: center;
        }
        & .is-disabled {
          opacity: 0.15;
          i.icon {
            color: $color1;
          }
        }
      }
    }

    &__Delete {
      position: absolute;
      width: 16px;
      height: 16;
      top: -30px;
      right: -25px;
      opacity: 0.1;
      cursor: pointer;
      transition: opacity 0.4s;

      &:hover {
        opacity: 0.8;
      }

      svg [pid="0"] {
        fill: #fff;
      }

      svg [pid="1"] {
        fill: $color2;
      }
    }
  }

  &__Content {
    position: relative;

    span {
      width: 100%;
      min-height: 112px;
      display: flex;
      justify-content: space-around;
      flex-wrap: wrap;
    }

    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      display: block;
      height: 112px;
      width: 80px;
      border: 1px dashed #B3B3B3;
      border-radius: 3px;
      opacity: 0;
    }

    &.empty {
      &::before {
        opacity: 1;
      }
    }

    &__Player {
      overflow: hidden;
      width: 112px;
      height: 112px;
      background: none;
      margin-bottom: $default_padding;
      flex-shrink: 0;
    }

    &.dragzone {
      box-shadow: 0 0 0 8px $color1;
      background: lighten($color1, 70%);
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/iDalgo-Section.vue